import { forkJoin, Observable, Observer } from 'rxjs'; 

// 文件选择服务
export class FileSvc {

	/*original 原图，compressed 压缩图*/ 
	sizeType;
	/*最多可以选择的图片张数，默认9*/ 
	count;
	/*album 从相册选图，camera 使用相机*/ 
	sourceType;
	/*根据文件拓展名过滤*/ 
	extension;

	defaultMediaOptions = { 
		count: 9, 
		mediaType: ['image', 'video'], 
		sourceType: ['album', 'camera'], 
		maxDuration: 10, 
		sizeType: ['original', 'compressed'], 
		camera: 'back' 
	};

	constructor(sourceType = ['camera'], sizeType = ['original'], count = 9) { 
		this.sourceType = sourceType; 
		this.sizeType = sizeType; 
		this.count = count; 
	}

	/** 
	 * 选择图片 
	 * @param options 
	 */ 
	chooseImage(options) { 
		return new Observable((observer) => { 
			console.log('FileSvc.chooseImage called with options:', options);
			if (!window.h5sdk) {
				console.log('FileSvc.chooseImage: no h5sdk');
				// 尝试使用原生文件输入作为备用方案
				this.useNativeFileInput(options).then(
					(files) => {
						observer.next(files);
						observer.complete();
					},
					(error) => {
						observer.error(error);
						observer.complete();
					}
				);
				return;
			}

			console.log('FileSvc.chooseImage: h5sdk exists', window.h5sdk);
			
			// 尝试不同的命名空间
			let chooseImageApi = null;
			if (window.h5sdk.tt && window.h5sdk.tt.chooseImage) {
				chooseImageApi = window.h5sdk.tt.chooseImage;
				console.log('FileSvc.chooseImage: using tt.chooseImage');
			} else if (window.h5sdk.lark && window.h5sdk.lark.chooseImage) {
				chooseImageApi = window.h5sdk.lark.chooseImage;
				console.log('FileSvc.chooseImage: using lark.chooseImage');
			} else if (window.tt && window.tt.chooseImage) {
				chooseImageApi = window.tt.chooseImage;
				console.log('FileSvc.chooseImage: using window.tt.chooseImage');
			} else if (window.lark && window.lark.chooseImage) {
				chooseImageApi = window.lark.chooseImage;
				console.log('FileSvc.chooseImage: using window.lark.chooseImage');
			} else {
				console.log('FileSvc.chooseImage: chooseImage not available');
				// 尝试使用原生文件输入作为备用方案
				this.useNativeFileInput(options).then(
					(files) => {
						observer.next(files);
						observer.complete();
					},
					(error) => {
						observer.error(error);
						observer.complete();
					}
				);
				return;
			}

			// 检测平台，PC 端仅支持相册
			const isPC = navigator.userAgent.includes('Windows') || navigator.userAgent.includes('Macintosh');
			const sourceType = options?.sourceType || (isPC ? ['album'] : ['album', 'camera']);
			
			console.log('FileSvc.chooseImage: calling chooseImage with sourceType:', sourceType);
			window.h5sdk.ready(() => {
				console.log('FileSvc.chooseImage: h5sdk ready');
				chooseImageApi({ 
					count: options?.count || this.count, //默认9 
					sizeType: options?.sizeType || this.sizeType, //可以指定是原图还是压缩图，默认二者都有 
					sourceType: sourceType, 
					cameraDevice: options?.cameraDevice || 'back',
					isSaveToAlbum: options?.isSaveToAlbum || '0',
					success: (res) => { 
						console.log('FileSvc.chooseImage: success', res);
						
						// 尝试将 ttfile:// 路径转换为 blob URL
						const convertTtfileToBlobUrl = (ttfilePath) => {
							return new Promise((resolve, reject) => {
								// 检查是否在飞书环境中
								if (window.h5sdk && (window.h5sdk.tt || window.tt)) {
									const tt = window.h5sdk.tt || window.tt;
									// 使用飞书的 getFileSystemManager API 读取文件
									if (tt.getFileSystemManager) {
										console.log('FileSvc.chooseImage: using getFileSystemManager to read file');
										tt.getFileSystemManager().readFile({
											filePath: ttfilePath,
											encoding: 'base64',
											success: (res) => {
												console.log('FileSvc.chooseImage: readFile success');
												// 从 base64 创建 blob URL
												const base64Data = res.data;
												const mime = ttfilePath.includes('.jpg') || ttfilePath.includes('.jpeg') ? 'image/jpeg' : 
														  ttfilePath.includes('.png') ? 'image/png' : 
														  ttfilePath.includes('.gif') ? 'image/gif' : 
														  ttfilePath.includes('.webp') ? 'image/webp' : 'image/jpeg';
												const blob = this.base64ToBlob(base64Data, mime);
												const blobUrl = URL.createObjectURL(blob);
												resolve(blobUrl);
											},
											fail: (err) => {
												console.error('FileSvc.chooseImage: readFile fail', err);
												// 如果读取失败，直接使用原始路径
												resolve(ttfilePath);
											}
										});
									} else {
										// 如果 getFileSystemManager 不可用，直接使用原始路径
										resolve(ttfilePath);
									}
								} else {
									// 非飞书环境，直接使用原始路径
									resolve(ttfilePath);
								}
							});
						};
						
						// 处理所有文件
						const processFiles = async () => {
							try {
								const processedFiles = [];
								for (const file of res.tempFiles) {
									const blobUrl = await convertTtfileToBlobUrl(file.path);
									processedFiles.push({
										...file,
										path: blobUrl, // 使用 blob URL 替换 ttfile:// 路径
										thumbnail: blobUrl // 使用 blob URL 作为 thumbnail
									});
								}
								observer.next(processedFiles);
								observer.complete();
							} catch (error) {
								console.error('FileSvc.chooseImage: processFiles error', error);
								// 处理失败时，使用原始文件
								const filesWithThumbnail = res.tempFiles.map(file => {
									return {
										...file,
										thumbnail: file.path
									};
								});
								observer.next(filesWithThumbnail);
								observer.complete();
							}
						};
						
						// 执行文件处理
						processFiles();
					},
					fail: (res) => {
						console.log('FileSvc.chooseImage: fail', res);
						const errorMsg = res.errMsg || res.errString || '选择图片失败';
						observer.error(new Error(errorMsg));
						observer.complete();
					}
				});
			});

		}); 
	}
	
	/**
	 * 使用原生文件输入作为备用方案
	 * @param options
	 */
	useNativeFileInput(options) {
		return new Promise((resolve, reject) => {
			console.log('FileSvc.useNativeFileInput: using native file input');
			const input = document.createElement('input');
			input.type = 'file';
			input.accept = 'image/*';
			input.multiple = true;
			input.style.display = 'none';
			document.body.appendChild(input);
			
			input.addEventListener('change', (e) => {
				const files = Array.from(e.target.files);
				console.log('FileSvc.useNativeFileInput: selected files', files);
				const tempFiles = files.map(file => ({
					path: URL.createObjectURL(file),
					size: file.size,
					name: file.name,
					// 在原生文件输入中，使用 path 作为 thumbnail
					thumbnail: URL.createObjectURL(file)
				}));
				document.body.removeChild(input);
				resolve(tempFiles);
			});
			
			input.addEventListener('cancel', () => {
				document.body.removeChild(input);
				reject(new Error('选择图片取消'));
			});
			
			input.click();
		});
	}
	
	/**
	 * 预览图片
	 * @param urls 图片地址列表
	 * @param current 当前显示的图片地址
	 */
	previewImage(urls, current) {
		return new Promise((resolve, reject) => {
			console.log('FileSvc.previewImage: previewing images', urls, current);
			
			// 确保 urls 是数组
			const imageUrls = Array.isArray(urls) ? urls : [urls];
			const currentUrl = current || imageUrls[0];
			
			console.log('FileSvc.previewImage: processed urls', imageUrls, currentUrl);
			
			// 检查是否在飞书环境中
			if (window.h5sdk) {
				console.log('FileSvc.previewImage: in h5sdk environment');
				
				// 在 h5sdk.ready 回调中调用
				window.h5sdk.ready(() => {
					console.log('FileSvc.previewImage: h5sdk ready');
					
					// 尝试不同的命名空间
					let previewImageApi = null;
					let apiNamespace = null;
					
					if (window.h5sdk.tt && window.h5sdk.tt.previewImage) {
						previewImageApi = window.h5sdk.tt.previewImage;
						apiNamespace = 'h5sdk.tt';
						console.log('FileSvc.previewImage: using h5sdk.tt.previewImage');
					} else if (window.h5sdk.lark && window.h5sdk.lark.previewImage) {
						previewImageApi = window.h5sdk.lark.previewImage;
						apiNamespace = 'h5sdk.lark';
						console.log('FileSvc.previewImage: using h5sdk.lark.previewImage');
					} else if (window.tt && window.tt.previewImage) {
						previewImageApi = window.tt.previewImage;
						apiNamespace = 'window.tt';
						console.log('FileSvc.previewImage: using window.tt.previewImage');
					} else if (window.lark && window.lark.previewImage) {
						previewImageApi = window.lark.previewImage;
						apiNamespace = 'window.lark';
						console.log('FileSvc.previewImage: using window.lark.previewImage');
					} else {
						console.log('FileSvc.previewImage: previewImage API not available in h5sdk');
						// 飞书环境但 API 不可用，使用浏览器备用方案
						this.previewImageInBrowser(imageUrls, currentUrl);
						resolve({ success: true, message: '使用浏览器预览' });
						return;
					}
					
					try {
						console.log('FileSvc.previewImage: calling', apiNamespace, 'with params:', {
							urls: imageUrls,
							current: currentUrl
						});
						
						previewImageApi({
							urls: imageUrls,
							current: currentUrl,
							success: (res) => {
								console.log('FileSvc.previewImage: success', res);
								resolve(res);
							},
							fail: (res) => {
								console.log('FileSvc.previewImage: fail', res);
								const errorMsg = res.errMsg || res.errString || '预览图片失败';
								console.log('FileSvc.previewImage: error message:', errorMsg);
								// 飞书预览失败时，尝试浏览器备用方案
								this.previewImageInBrowser(imageUrls, currentUrl);
								resolve({ success: true, message: '使用浏览器预览' });
							}
						});
					} catch (error) {
						console.error('FileSvc.previewImage: exception when calling API', error);
						// 调用 API 出错时，使用浏览器备用方案
						this.previewImageInBrowser(imageUrls, currentUrl);
						resolve({ success: true, message: '使用浏览器预览' });
					}
				});
			} else {
				console.log('FileSvc.previewImage: not in h5sdk environment, using browser fallback');
				// 非飞书环境，使用浏览器备用方案
				this.previewImageInBrowser(imageUrls, currentUrl);
				resolve({ success: true, message: '使用浏览器预览' });
			}
		});
	}
	
	/**
	 * 在浏览器中预览图片
	 * @param urls 图片地址列表
	 * @param current 当前显示的图片地址
	 */
	previewImageInBrowser(urls, current) {
		console.log('FileSvc.previewImageInBrowser: previewing images in browser', urls, current);
		
		// 创建预览容器
		const previewContainer = document.createElement('div');
		previewContainer.style.cssText = `
			position: fixed;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-color: rgba(0, 0, 0, 0.9);
			display: flex;
			justify-content: center;
			align-items: center;
			z-index: 9999;
			cursor: pointer;
		`;
		
		// 创建图片元素
		const img = document.createElement('img');
		img.src = current || urls[0];
		img.style.cssText = `
			max-width: 90%;
			max-height: 90%;
			object-fit: contain;
		`;
		
		// 添加图片到容器
		previewContainer.appendChild(img);
		
		// 点击容器关闭预览
		previewContainer.addEventListener('click', () => {
			document.body.removeChild(previewContainer);
		});
		
		// 按ESC键关闭预览
		const handleKeyDown = (e) => {
			if (e.key === 'Escape') {
				document.body.removeChild(previewContainer);
				document.removeEventListener('keydown', handleKeyDown);
			}
		};
		document.addEventListener('keydown', handleKeyDown);
		
		// 添加到文档
		document.body.appendChild(previewContainer);
	}

	/** 
	 * 压缩图片 
	 * @param src 
	 * @param quality 
	 */ 
	compressImage(src, quality) { 
		return new Observable((observer) => { 
			console.log('FileSvc.compressImage: compressing image', src, quality);
			
			// 尝试不同的命名空间
			let compressImageApi = null;
			if (window.h5sdk && window.h5sdk.tt && window.h5sdk.tt.compressImage) {
				compressImageApi = window.h5sdk.tt.compressImage;
				console.log('FileSvc.compressImage: using tt.compressImage');
			} else if (window.h5sdk && window.h5sdk.lark && window.h5sdk.lark.compressImage) {
				compressImageApi = window.h5sdk.lark.compressImage;
				console.log('FileSvc.compressImage: using lark.compressImage');
			} else if (window.tt && window.tt.compressImage) {
				compressImageApi = window.tt.compressImage;
				console.log('FileSvc.compressImage: using window.tt.compressImage');
			} else if (window.lark && window.lark.compressImage) {
				compressImageApi = window.lark.compressImage;
				console.log('FileSvc.compressImage: using window.lark.compressImage');
			} else {
				console.log('FileSvc.compressImage: compressImage not available');
				// 如果 compressImage API 不可用，返回原始图片
				observer.next({ 
					path: src, 
					size: 0, 
					name: `compressed_${Date.now()}.jpg`,
					// 使用 path 作为 thumbnail
					thumbnail: src
				}); 
				observer.complete(); 
				return;
			}
			
			// 调用 compressImage API
			compressImageApi({
				src: src,
				quality: quality || 80,
				success: (res) => {
					console.log('FileSvc.compressImage: success', res);
					observer.next({ 
						path: res.tempFilePath, 
						size: 0, 
						name: `compressed_${Date.now()}.jpg`,
						// 使用压缩后的路径作为 thumbnail
						thumbnail: res.tempFilePath
					}); 
					observer.complete(); 
				},
				fail: (res) => {
					console.log('FileSvc.compressImage: fail', res);
					// 如果压缩失败，返回原始图片
					observer.next({ 
						path: src, 
						size: 0, 
						name: `compressed_${Date.now()}.jpg`,
						// 使用 path 作为 thumbnail
						thumbnail: src
					}); 
					observer.complete(); 
				}
			});
		}); 
	}

	chooseMedia(options) {

		Object.assign(options, this.defaultMediaOptions);

		return new Observable((observer) => { 
			if (!window.h5sdk) {
				observer.error(new Error('请在飞书客户端中打开此应用'));
				observer.complete();
				return;
			}

			window.h5sdk.ready(() => {
				window.h5sdk.tt.chooseImage({ 
					count: options?.count, 
					sourceType: options?.sourceType, 
					sizeType: options?.sizeType, 
					success: (res) => { 
						if (options?.compressed) { 
							const sources = []; 
							res.tempFiles.forEach(file => { 
								sources.push(this.compressImage(file.path, options.compressed.compressedImage)); 
							}); 
							forkJoin(sources).subscribe((fileList) => { 
								observer.next(fileList); 
								observer.complete(); 
							}); 
						} else { 
							// 为每个文件添加一个 thumbnail 属性，用于在浏览器中显示
							const filesWithThumbnail = res.tempFiles.map(file => {
								return {
									...file,
									// 在飞书环境中，使用 path 作为 thumbnail
									// 飞书客户端会处理 ttfile:// 协议
									thumbnail: file.path
								};
							});
							observer.next(filesWithThumbnail); 
							observer.complete(); 
						} 
					}, 
					fail: (e) => { 
						console.error(e); 
						observer.error(new Error('选择媒体文件失败'));
						observer.complete();
					} 
				});
			});

		}); 
	}

	compressVideo(file, compressed) { 
		return new Observable((observer) => { 
			// 飞书环境下的压缩视频实现
			observer.next({ 
				...file, 
				path: file.path 
			}); 
			observer.complete(); 
		}); 
	}

	/**
	 * 获取图片信息
	 * @param src 图片的路径
	 */
	getImageInfo(src) {
		return new Observable((observer) => {
			console.log('FileSvc.getImageInfo: getting image info', src);
			
			// 尝试不同的命名空间
			let getImageInfoApi = null;
			if (window.h5sdk && window.h5sdk.tt && window.h5sdk.tt.getImageInfo) {
				getImageInfoApi = window.h5sdk.tt.getImageInfo;
				console.log('FileSvc.getImageInfo: using tt.getImageInfo');
			} else if (window.h5sdk && window.h5sdk.lark && window.h5sdk.lark.getImageInfo) {
				getImageInfoApi = window.h5sdk.lark.getImageInfo;
				console.log('FileSvc.getImageInfo: using lark.getImageInfo');
			} else if (window.tt && window.tt.getImageInfo) {
				getImageInfoApi = window.tt.getImageInfo;
				console.log('FileSvc.getImageInfo: using window.tt.getImageInfo');
			} else if (window.lark && window.lark.getImageInfo) {
				getImageInfoApi = window.lark.getImageInfo;
				console.log('FileSvc.getImageInfo: using window.lark.getImageInfo');
			} else {
				console.log('FileSvc.getImageInfo: getImageInfo not available');
				observer.error(new Error('获取图片信息功能不可用'));
				observer.complete();
				return;
			}
			
			// 调用 getImageInfo API
			getImageInfoApi({
				src: src,
				success: (res) => {
					console.log('FileSvc.getImageInfo: success', res);
					observer.next(res);
					observer.complete();
				},
				fail: (res) => {
					console.log('FileSvc.getImageInfo: fail', res);
					const errorMsg = res.errMsg || res.errString || '获取图片信息失败';
					observer.error(new Error(errorMsg));
					observer.complete();
				}
			});
		});
	}

	/**
	 * 将 base64 字符串转换为 blob 对象
	 * @param base64Data
	 * @param contentType
	 * @returns {Blob}
	 */
	base64ToBlob(base64Data, contentType) {
		const byteCharacters = atob(base64Data);
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);
		return new Blob([byteArray], { type: contentType });
	}

 }

 export const fileSvc = new FileSvc(['camera', 'album']);